"use client"

import { useCallback, useEffect, useRef, useState } from 'react'

import { useAuth } from '@/contexts/auth-context'
import { useWorkflowApi } from '@/hooks/use-workflow-api'
import { env } from '@/lib/env'
import { subscribeToConversationChanges } from '@gorilla-works/messages'

import { onStageMessagesChange } from '@/lib/stage-message-events'
import { onTabRefocus } from '@/lib/on-tab-refocus'

export interface StageMessageCount {
  unread: number
  hasImportant: boolean
}

// Shape returned by GET /api/conversations/my-states (MessagesService).
interface ConversationState {
  entityId: string
  messageCount: number
  unreadCount: number
  hasImportantUnread: boolean
  isSubscribed: boolean
}

// One subscribed assignment's contribution, retained between fetches so an optimistic read can zero it
// and the stage totals can be rebuilt without another round-trip.
interface AssignmentState {
  stage: string
  unread: number
  hasImportant: boolean
}

const REFRESH_INTERVAL_MS = 5 * 60 * 1000
// After a read/unread/mute, my-states is the source of truth — but the watermark write races the refetch,
// so wait before reconciling. Long enough that the write has usually landed by the FIRST reconcile: a read
// then confirms in a single pass instead of polling my-states repeatedly, and a mark-UNREAD — which has no
// optimistic value and only shows once its write lands — is caught on that first pass instead of lagging
// to the 5-minute interval. Reads still feel instant: their badge is zeroed optimistically up front, so a
// longer confirm delay is invisible.
const RECONCILE_DELAY_MS = 1500
// Backstop only — there is no self-scheduled poll loop. A stuck optimistic read is un-suppressed after
// this many action-driven reconciles still show it unread, letting my-states win, in case a burst of user
// actions keeps reconciling before the 5-min full refresh (which drops the suppression outright) runs. So
// a read whose write genuinely failed self-heals either way.
const MAX_RECONCILE_ATTEMPTS = 6
// Caps the assignment-id set handed to the wfh-search in step 2 — NOT the my-states read, which is bounded
// server-side. Azure AI Search rejects $top over 1000 and chokes on a very long OData filter, and the
// unread-assignment id set is unbounded in principle (a heavy PM), so cap it: the search degrades to a
// partial count rather than failing outright, and the truncation is logged, not swallowed.
const MAX_STAGE_COUNT_ASSIGNMENTS = 1000

/**
 * Per-stage unread MESSAGE counts for the current user, keyed by stage code (lowercase, matching
 * `stageCounts`). Two bounded reads, joined in memory:
 *   1. GET /conversations/my-states — my subscribed Assignment conversations with unread. Scoped to my
 *      OWN assignments (the endpoint filters on my `user:` key) and bounded SERVER-SIDE; this hook does
 *      not cap the read. MAX_STAGE_COUNT_ASSIGNMENTS caps only the id set handed to step 2.
 *   2. wfh-search for those assignment ids' Stage (`FboAssignmentId eq …`, a bounded set).
 * Unread is then summed by Stage and retained per-assignment so read-state changes can update the
 * badges immediately (see below).
 *
 * Skipped entirely when notifications are off — that saves BOTH calls, which the modal promises.
 * Refreshes on the same 5-minute + visibility cadence as the row counts and the badge tick so the three
 * settle together. Runs on the Requests tabs (ATS/VMS/CR) too: the WO stage strip is shown there, and the
 * badges are the reader's OWN assignment unread per stage — view-independent — so they belong there.
 *
 * Hybrid read-state updates: subscribes to {@link onStageMessagesChange} (fired by the grid's read /
 * unread / mute actions and by opening a panel). A confirmed read zeroes those assignments instantly
 * for snappy feedback AND suppresses them from the reconcile until the mark-read write lands, so a
 * reconcile that races the POST cannot resurrect the badge it just zeroed.
 */
export function useStageMessageCounts(enabled: boolean): Record<string, StageMessageCount> {
  const { token } = useAuth()
  const { searchWorkflowItems } = useWorkflowApi()
  const [counts, setCounts] = useState<Record<string, StageMessageCount>>({})

  const searchRef = useRef(searchWorkflowItems)
  searchRef.current = searchWorkflowItems
  const tokenRef = useRef(token)
  tokenRef.current = token
  // Ignore a stale response when a newer refresh has already started.
  const requestIdRef = useRef(0)
  // The last known per-assignment breakdown, so counts can be rebuilt after an optimistic read.
  const assignmentStatesRef = useRef<Map<string, AssignmentState>>(new Map())
  const reconcileTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Assignments the reader just marked read (id → reconcile attempts). Their badge is zeroed optimistically
  // and EXCLUDED from my-states until the watermark write lands — otherwise a reconcile that beats the
  // mark-read POST re-reads the old unread and resurrects the badge for up to a full refresh interval.
  const optimisticReadRef = useRef<Map<string, number>>(new Map())
  // Placeholder so scheduleReconcile (declared before refresh) always calls the latest refresh via a ref.
  const refreshRef = useRef<(fullRefresh?: boolean) => void>(() => {})

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Restart the 5-minute periodic full refresh. Called on mount, and again whenever a panel action forces
  // a fresh full recalc, so the interval counts from that recalc rather than redundantly re-firing on its
  // old schedule moments later.
  const restartInterval = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    intervalRef.current = setInterval(() => refreshRef.current(true), REFRESH_INTERVAL_MS)
  }, [])

  // The two call sites share one timer, so a `full` request in the debounce window must survive a later
  // partial one: OR the flag across calls so a panel-requested full recalc always runs, instead of a grid
  // reconcile scheduled moments later silently downgrading it to partial (and never resetting the clock).
  const pendingFullReconcileRef = useRef(false)
  // Debounced recalc. `full` re-reads my-states as the source of truth (clears the optimistic suppression
  // and re-searches stages) AND resets the 5-min clock — used for a panel mark-read/unread so the stage
  // badges reflect it now instead of waiting out the interval. The grid path passes false (reconcile only,
  // reusing the cache and the optimistic overlay). NOTE: a full refresh clears the WHOLE suppression map,
  // so an unrelated grid mark-read whose write is still in flight can briefly resurrect until the next
  // action or the 5-min refresh re-zeroes it — an accepted trade for one fixed reconcile delay.
  const scheduleReconcile = useCallback((full = false) => {
    pendingFullReconcileRef.current = pendingFullReconcileRef.current || full
    if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current)
    reconcileTimerRef.current = setTimeout(() => {
      const runFull = pendingFullReconcileRef.current
      pendingFullReconcileRef.current = false
      if (runFull) restartInterval()
      refreshRef.current(runFull)
    }, RECONCILE_DELAY_MS)
  }, [restartInterval])

  // TEMP bridge — remove when this app is relocked to @gorilla-works/messages >= 1.0.15. A panel
  // per-message mark on the installed 1.0.14 fires a conversation-change signal with NO settled unread
  // (see the subscription below), so the stage badge must reconcile from my-states — but my-states can lag
  // the just-committed watermark past a single RECONCILE_DELAY_MS pass, which left the badge stale until the
  // 5-min refresh. Fire a few STAGGERED refreshes so it converges within seconds once my-states catches up.
  // Event-driven and bounded (three attempts, collapsed across a burst) and self-terminating — NOT the
  // standing my-states poll that was removed. 1.0.15 carries the unread in the signal, so the fast path
  // below runs instead and this never fires.
  const convergeTimersRef = useRef<ReturnType<typeof setTimeout>[]>([])
  const scheduleConvergingRefresh = useCallback(() => {
    for (const timer of convergeTimersRef.current) clearTimeout(timer)
    // Count the next 5-min tick from this event, so it doesn't redundantly re-fire right after — exactly
    // what the reconcile path did.
    restartInterval()
    convergeTimersRef.current = [
      // Full first: clears the panel-open optimistic suppression and resolves the newly-unread stage.
      setTimeout(() => refreshRef.current(true), RECONCILE_DELAY_MS),
      // Then re-read my-states a couple more times (reusing the stage cache) so a slow watermark
      // propagation still lands within seconds instead of waiting out the 5-min interval.
      setTimeout(() => refreshRef.current(false), 8000),
      setTimeout(() => refreshRef.current(false), 20000),
    ]
  }, [restartInterval])

  // Rebuild the stage totals from the retained per-assignment states. Cheap (the set is my own unread
  // subscriptions), and recomputing hasImportant from scratch keeps it correct when one assignment in a
  // stage is read but another important one remains.
  const rebuildCounts = useCallback(() => {
    const grouped: Record<string, StageMessageCount> = {}
    for (const { stage, unread, hasImportant } of assignmentStatesRef.current.values()) {
      if (!stage || unread <= 0) continue
      const bucket = grouped[stage] ?? (grouped[stage] = { unread: 0, hasImportant: false })
      bucket.unread += unread
      bucket.hasImportant = bucket.hasImportant || hasImportant
    }
    setCounts(grouped)
  }, [])

  const refresh = useCallback(async (fullRefresh = false) => {
    if (!enabled) {
      // Invalidate any refresh already in flight, or a response that started while the gate was open can
      // land after it closed (staleness guard at ~123 keys off requestIdRef) and repaint counts for a
      // kill-switched or notifications-off user.
      ++requestIdRef.current
      assignmentStatesRef.current = new Map()
      optimisticReadRef.current.clear()
      setCounts({})
      return
    }
    const authToken = tokenRef.current
    if (!authToken) return

    const currentRequestId = ++requestIdRef.current
    try {
      // 1) My subscribed Assignment conversations with unread — bounded to my own assignments.
      const statesResponse = await fetch(
        `${env.messagesApiUrl}/conversations/my-states?entityTypeId=Assignment`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      if (!statesResponse.ok) return
      const states: ConversationState[] = await statesResponse.json()
      if (currentRequestId !== requestIdRef.current) return

      const optimisticRead = optimisticReadRef.current
      // A full refresh (5-min interval / tab-return / gate flip) is the source of truth: by now the
      // mark-read write has long since landed or failed, so drop any optimistic read-suppression and let
      // my-states decide. This self-heals a badge left wrong by a read whose write never landed — the job
      // the reconcile poll loop used to do, now without the burst of my-states calls.
      if (fullRefresh) optimisticRead.clear()

      const unreadByAssignment = new Map<string, StageMessageCount>()
      const unreadIds = new Set<string>()
      for (const state of states) {
        if (!(state.isSubscribed && state.unreadCount > 0)) continue
        const id = String(state.entityId)
        unreadIds.add(id)
        // Optimistically read, and the mark-read write may not show in my-states yet (the on-open read is
        // fire-and-forget and can lag). Keep the badge zeroed by skipping it rather than resurrecting the
        // old unread — but do NOT self-schedule a re-poll waiting for the write; that re-poll WAS the
        // my-states burst. It clears on the next reconcile where my-states shows it read (below), or on the
        // 5-min full refresh (above). The bounded attempt count is a backstop that lets my-states win if a
        // read genuinely failed and later actions keep reconciling before a full refresh clears it.
        const attempts = optimisticRead.get(id)
        if (attempts !== undefined && attempts < MAX_RECONCILE_ATTEMPTS) {
          optimisticRead.set(id, attempts + 1)
          continue
        }
        optimisticRead.delete(id)
        unreadByAssignment.set(id, {
          unread: state.unreadCount,
          hasImportant: state.hasImportantUnread,
        })
      }
      // Any tracked id my-states no longer reports as unread has had its write land — stop tracking it so a
      // later new message on that thread bumps the badge again.
      for (const id of [...optimisticRead.keys()]) if (!unreadIds.has(id)) optimisticRead.delete(id)

      if (unreadByAssignment.size === 0) {
        assignmentStatesRef.current = new Map()
        setCounts({})
        return
      }

      // 2) Stage for each unread assignment. The stage is stable across a read/unread's short reconcile
      // window, so REUSE the retained stage from the last read rather than re-searching it — a reconcile
      // then usually issues NO wfh-search at all, only my-states. Only an assignment we have no cached
      // stage for (newly unread) is searched; a `fullRefresh` (interval / tab-return / gate flip) drops the
      // cache and re-searches everything, so a stage move is still picked up. FboAssignmentId is NUMERIC in
      // the index, so search.in (string-only) matches nothing — the eq/or chain (`FboAssignmentId eq <n>`,
      // unquoted) is used, and only numeric ids are searched so a stray entityId can't malform the filter.
      const prevStates = assignmentStatesRef.current
      const nextStates = new Map<string, AssignmentState>()
      const idsToSearch: string[] = []
      for (const [id, info] of unreadByAssignment) {
        const cachedStage = fullRefresh ? undefined : prevStates.get(id)?.stage
        if (cachedStage) {
          nextStates.set(id, { stage: cachedStage, unread: info.unread, hasImportant: info.hasImportant })
        } else if (/^\d+$/.test(id)) {
          idsToSearch.push(id)
        }
      }

      if (idsToSearch.length > 0) {
        // CAP so $top and the OData filter stay under Azure Search's limits — a truncation is logged, not
        // swallowed, so the count degrades to partial rather than the request failing outright.
        if (idsToSearch.length > MAX_STAGE_COUNT_ASSIGNMENTS) {
          console.warn(
            `useStageMessageCounts: ${idsToSearch.length} unread assignment conversations exceeds the ` +
            `${MAX_STAGE_COUNT_ASSIGNMENTS} cap; stage badges reflect only the first ${MAX_STAGE_COUNT_ASSIGNMENTS}.`
          )
        }
        const assignmentIds = idsToSearch.slice(0, MAX_STAGE_COUNT_ASSIGNMENTS)
        const idClause = assignmentIds.map(id => `FboAssignmentId eq ${id}`).join(' or ')
        const filter = `DocumentType eq 'Assignment' and (${idClause})`
        const result = await searchRef.current(
          undefined, undefined, [], 1, assignmentIds.length, '*', 'FboAssignmentId,Stage', filter
        )
        if (currentRequestId !== requestIdRef.current) return

        // Keyed the same way as `stageCounts` (stage value, lowercased).
        for (const row of result.data ?? []) {
          const stage = String((row as any).stage ?? '').toLowerCase()
          const info = unreadByAssignment.get(String((row as any).fboAssignmentId))
          if (!stage || !info) continue
          nextStates.set(String((row as any).fboAssignmentId), {
            stage,
            unread: info.unread,
            hasImportant: info.hasImportant,
          })
        }
      }

      assignmentStatesRef.current = nextStates
      rebuildCounts()
    } catch {
      // Best-effort — a tab message badge isn't worth surfacing an error for.
    }
  }, [enabled, rebuildCounts])

  refreshRef.current = refresh

  // Refresh when the gate flips (notifications toggled) OR when the auth token
  // first resolves — the fetch reads the token, so a token that arrives after mount must kick off a
  // refresh rather than waiting out the 5-minute interval.
  useEffect(() => {
    refreshRef.current(true)
  }, [enabled, token])

  // 5-minute poll + fresh-on-focus, set up once so nothing tears it down — same cadence as the row
  // counts and the badge tick.
  useEffect(() => {
    restartInterval()
    // Fresh-on-return only for a genuine tab switch — see onTabRefocus. A bare visibilitychange
    // handler fired my-states on every spurious iframe reattach (≈ every mouse move). A full refresh so a
    // stage move while the tab was hidden is picked up.
    const stopRefocus = onTabRefocus(() => refreshRef.current(true))
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      stopRefocus()
    }
  }, [restartInterval])

  // Hybrid read-state updates. A confirmed read zeroes those assignments in the retained map instantly
  // and marks them to be suppressed from the reconcile until the write lands; every change (read, unread,
  // mute) also schedules a single debounced reconcile from my-states.
  useEffect(() => {
    const unsubscribe = onStageMessagesChange(({ entityTypeId, entityIds, optimisticRead }) => {
      if (entityTypeId !== 'Assignment') return
      if (optimisticRead) {
        let touched = false
        for (const id of entityIds) {
          const key = String(id)
          // Track it so the reconcile keeps the badge zeroed until the mark-read write lands (see refresh).
          optimisticReadRef.current.set(key, 0)
          const state = assignmentStatesRef.current.get(key)
          if (state && state.unread > 0) {
            state.unread = 0
            state.hasImportant = false
            touched = true
          }
        }
        if (touched) rebuildCounts()
      } else {
        // A mark-unread or mute: stop suppressing these — my-states is now the truth we want to show.
        for (const id of entityIds) optimisticReadRef.current.delete(String(id))
      }
      scheduleReconcile()
    })
    return () => {
      unsubscribe()
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current)
    }
  }, [rebuildCounts, scheduleReconcile])

  // Panel per-message mark-read/unread fires the package's conversation-change signal. When it carries the
  // settled unread (@gorilla-works/messages >= 1.0.15, third arg), apply it straight to the cached
  // assignment's badge — a plain reconcile would re-read my-states, which lags the just-committed write and
  // can leave the badge stale until the 5-min refresh (the bug where the inline icon updated but the stage
  // count didn't). The installed 1.0.14 signal carries NO unread value, so that fast path can't run yet; it
  // falls through to scheduleConvergingRefresh (temp bridge) below. The 5-min refresh and tab-return still
  // reconcile from my-states later, so this stays self-healing either way.
  useEffect(() => {
    // Cast: installed 1.0.14 types the listener as 2-arg (no unreadCount); >= 1.0.15 adds the third arg.
    // Typing the params here and casting to the package's current listener type reads the settled unread
    // when present and compiles against either version. Drop the cast at the 1.0.15 relock.
    const onConversationChange = (
      changedEntityTypeId: string,
      entityId: string,
      unreadCount?: number
    ) => {
      if (changedEntityTypeId !== 'Assignment') return

      const key = entityId != null ? String(entityId) : null
      const state = key !== null ? assignmentStatesRef.current.get(key) : undefined
      if (key !== null && unreadCount != null && state) {
        if (unreadCount > 0) {
          optimisticReadRef.current.delete(key) // a deliberate unread lifts any read-suppression
          state.unread = unreadCount
        } else {
          optimisticReadRef.current.set(key, 0) // marked read: suppress until my-states agrees, as the grid does
          state.unread = 0
          state.hasImportant = false
        }
        rebuildCounts()
        return
      }

      scheduleConvergingRefresh()
    }
    const unsubscribe = subscribeToConversationChanges(
      onConversationChange as Parameters<typeof subscribeToConversationChanges>[0]
    )
    return () => {
      unsubscribe()
      for (const timer of convergeTimersRef.current) clearTimeout(timer)
      convergeTimersRef.current = []
    }
  }, [rebuildCounts, scheduleConvergingRefresh])

  return counts
}
